import React, { useEffect, useMemo, useState } from 'react';

import { useFormik } from 'formik';

import {
  adminDeleteContract,
  ConfirmationTypes,
  getLongTermContractAttributes,
  getTypeOfUser,
  PharmacistLegendVariant,
  pharmacyDeleteContract,
  resetLongTermContractAttributes,
  TypeOfUser,
  WorkshiftStatus,
} from '@pharmaplan/common';

import { resetDialog, setDialog } from '../../reducers/dialogReducer';
import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import useViewEditContract from '../workshift/useViewEditContract';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import ContractDeleteMessage from '../../components/SelfService/WorkshiftSummary/WorkshiftSummaryList/SummaryContent/ContractDeleteMessage';
import useViewAllLongTermContracts from '../ViewAllLongTermContracts/useViewAllLongTermContracts';
import useUpdateContract from '../LongTermContract/useUpdateContract';

interface IUseMultiShiftDelete {
  longTermShiftContractId: string;
}

const useMultiShiftDelete = ({
  longTermShiftContractId,
}: IUseMultiShiftDelete) => {
  const dispatch = useAppDispatch();
  const { setAttributes } = useUpdateContract();
  const { intervalsInitial } = useViewEditContract();

  const userType = useAppSelector(getTypeOfUser);

  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const { startDate: savedStartDate, endDate: savedEndDate } = longTermAttributes ?? {};

  const { contract, getAPI } = useViewAllLongTermContracts({
    longTermContractId: longTermShiftContractId,
    type: PharmacistLegendVariant.workshift,
  });

  const {
    data,
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    allowanceNotes,
    contractStartDate,
    contractEndDate,
  } = contract ?? {};

  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const filteredData = useMemo(
    () =>
      data.filter((item) =>
        item.status !== WorkshiftStatus.Booked),
    [data],
  );

  const isAllSelected = selectedIds?.length === filteredData?.length;

  const deleteApis = {
    [TypeOfUser.admin]: adminDeleteContract,
    [TypeOfUser.superAdmin]: adminDeleteContract,
    [TypeOfUser.pharmacy]: pharmacyDeleteContract,
  };

  const handleDelete = () => {
    dispatch(
      deleteApis[userType as keyof typeof deleteApis]({
        longTermShiftContractId,
        workshiftIds: selectedIds,
      }),
    );
  };

  const confirmMultipleDelete = () => {
    dispatch(
      setDialog({
        heading: {
          title: '',
        },
        Component: (
          <ConfirmationModal
            type={ConfirmationTypes.deleteMultipleContracts}
            customMessage={
              <ContractDeleteMessage totalShiftCount={selectedIds.length} />
            }
            confirmAction={handleDelete}
          />
        ),
        showCloseButton: false,
      }),
    );
  };

  const formik = useFormik({
    initialValues: {
      startDate: savedStartDate ?? null,
      endDate: savedEndDate ?? null,
      weekDayPaidHourlyRate,
      weekendPaidHourlyRate,
      allowanceNotes,
    },
    onSubmit: confirmMultipleDelete,
  });

  const handleCheckboxClick = (shiftId: string) =>
    () => {
      if (selectedIds.includes(shiftId)) {
        setSelectedIds(selectedIds.filter((item) =>
          item !== shiftId));
      } else {
        setSelectedIds((prev) =>
          [...prev, shiftId]);
      }
    };

  const handleClose = () => {
    dispatch(resetDialog());
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(() =>
        filteredData.map((item) =>
          item.workShiftId ?? ''));
    }
  };

  useEffect(() => {
    formik.setValues({
      ...formik.values,
      startDate: savedStartDate ?? null,
      endDate: savedEndDate ?? null,
      ...intervalsInitial(data),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract.data, savedEndDate, savedStartDate]);

  useEffect(() => {
    setAttributes(contractStartDate, contractEndDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractStartDate, contractEndDate]);

  useEffect(
    () =>
      () => {
        dispatch(resetLongTermContractAttributes());
      },
    [],
  );

  return {
    handleCheckboxClick,
    formik,
    getAPI,
    handleSubmit: formik.handleSubmit,
    handleClose,
    selectedIds,
    handleSelectAll,
    isAllSelected,
    data,
    startDate: contractStartDate,
  };
};

export default useMultiShiftDelete;
